import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Form, InputNumber, Tag, Tooltip } from "antd";
import dayjs from "dayjs";
import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import { GoLinkExternal } from "react-icons/go";
import { colors } from "variables";
import CTable from "components/CTable";
import useFilter from "components/Filter";
import {
  getGridify,
  getStatus,
  keyboardEvents,
  showConfirm,
  showError,
  showSuccess,
  useNewState,
} from "helpers/utility";
import useAjax from "helpers/ajax";
import Toolbar from "components/Toolbar";
import { DEFAULT_PAGE_SIZE } from "constants";
import { filterTypes } from "components/Filter/constants";
import { useAuth } from "helpers/auth";
import confirm from "antd/lib/modal/confirm";

const Devices = () => {
  const { get, del } = useAjax();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useNewState({
    current: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const {
    user: { scopes },
  } = useAuth();

  const [form] = Form.useForm();
  const showRenewConfirm = (days) =>
    new Promise((resolve) => {
      confirm({
        icon: <ExclamationCircleOutlined />,
        title: "Renew license",
        content: (
          <div>
            It will extend the license's expiry to specified number of days.
            <Form
              layout="inline"
              form={form}
              initialValues={{ noOfDays: days }}
            >
              <Form.Item
                name="noOfDays"
                label="No of days"
                rules={[{ required: true }]}
              >
                <InputNumber precision={0} autoFocus />
              </Form.Item>
            </Form>
          </div>
        ),
        okText: "Yes",
        okType: "danger",
        cancelText: "No",
        onOk() {
          form.validateFields().then((values) => {
            const { noOfDays } = values;
            return resolve({ noOfDays });
          });
        },
        onCancel() {},
      });
    });

  const onDelete = (record) => {
    showConfirm("Data once deleted can not be restored.").then(() => {
      del("api/v1/devices", { key: record.activationID })
        .then(() => {
          const pager = { ...pagination, current: 1 };
          loadData(pager);
        })
        .catch((err) => {
          showError(err);
        });
    });
  };

  const onRenewLicense = (record) => {
    showRenewConfirm(record.noOfDays).then(({ noOfDays }) => {
      get("api/v1/licenses/renew", {
        activationID: record.activationID,
        days: noOfDays,
      })
        .then(() => {
          const pager = { ...pagination, current: 1 };
          loadData(pager);
          showSuccess("License has been renewed successfully.");
        })
        .catch((err) => {
          showError(err);
        });
    });
  };

  const onRenewHistory = (record) => {
    navigate(`/devices/licenses/history`, {
      state: record,
    });
  };

  const columns = [
    {
      title: "Actions",
      width: "65px",
      align: "center",
      render: (text, record) => (
        <Button.Group>
          <Tooltip placement="topLeft" title="Renew">
            <Button
              type="primary"
              size="small"
              icon={<SyncOutlined />}
              onClick={() => onRenewLicense(record)}
              disabled={!record.expired}
            />
          </Tooltip>
          <Tooltip placement="topLeft" title="Edit">
            <Button
              type="primary"
              size="small"
              icon={<EditOutlined />}
              onClick={() => {
                navigate(`/devices/devices/edit/${record.activationID}`, {
                  state: record,
                });
              }}
              disabled={
                record.authorizationKey !== null &&
                record.authorizationKey !== ""
              }
            />
          </Tooltip>
          <Tooltip placement="topLeft" title="Delete">
            <Button
              type="danger"
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => onDelete(record)}
              disabled={
                record.authorizationKey !== null &&
                record.authorizationKey !== ""
              }
            />
          </Tooltip>
        </Button.Group>
      ),
    },
    {
      title: "Distributor name",
      dataIndex: "companyName",
      sorter: true,
      ...useFilter(),
    },
    {
      title: "Authorization key",
      dataIndex: "authorizationKey",
      sorter: true,
      ...useFilter(),
      render: (text, record) => {
        const { renewed, expired } = record;
        if (renewed) {
          return (
            <a {...keyboardEvents(() => onRenewHistory(record))}>
              <Tooltip placement="top" title="View renew history">
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "5px",
                    color: expired ? "#ff4d4f" : colors.primary,
                  }}
                >
                  {text}
                  <GoLinkExternal />
                </div>
              </Tooltip>
            </a>
          );
        }
        return text;
      },
    },
    {
      title: "Serial no.",
      dataIndex: "serialNo",
      sorter: true,
      ...useFilter(),
    },
    {
      title: "Model no.",
      dataIndex: "modelNo",
      sorter: true,
      ...useFilter(),
    },
    {
      title: "Activation date",
      dataIndex: "activatedOn",
      sorter: true,
      ...useFilter({ type: filterTypes.DATE }),
      render: (text) => (text ? dayjs(text).format("MM/DD/YYYY") : ""),
    },
    {
      title: "Expiry date",
      dataIndex: "expiryDate",
      sorter: true,
      ...useFilter({ type: filterTypes.DATE }),
      render: (text) => (text ? dayjs(text).format("MM/DD/YYYY") : ""),
    },
    {
      title: "Customer name",
      dataIndex: "customerName",
      sorter: true,
      ...useFilter(),
    },
    {
      title: "Created by",
      dataIndex: "createdByName",
      sorter: true,
    },
    {
      title: "Created date",
      dataIndex: "createdOn",
      sorter: true,
      ...useFilter({ type: filterTypes.DATE }),
      render: (text) => (text ? dayjs(text).format("MM/DD/YYYY") : ""),
    },
    {
      title: "Compatible",
      dataIndex: "compatible",
      sorter: true,
      ...useFilter({ type: filterTypes.YESNO }),
      render: (text) =>
        text ? <Tag color="green">Yes</Tag> : <Tag color="red">No&nbsp;</Tag>,
    },
    {
      title: "Status",
      dataIndex: "active",
      width: "100px",
      sorter: true,
      ...useFilter({ type: filterTypes.YESNO }),
      render: (text) => getStatus(text),
    },
  ];

  const loadData = (pager, filters, sorter) => {
    const gridify = getGridify(pager, filters, sorter, [
      {
        field: "expiryRank",
        order: "descend",
      },
      {
        field: "expiryDate",
        order: "ascend",
      },
    ]);
    get("api/v1/devices", { ...gridify })
      .then((resp) => {
        setData(resp.data);
        setPagination({
          ...pager,
          total: resp.count,
        });
      })
      .catch((err) => {
        showError(err);
      })
      .finally(() => setLoading(false));
  };

  const onTableChange = (pager, filters, sorter) => {
    loadData(pager, filters, sorter);
  };

  useEffect(() => {
    loadData(pagination);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <Toolbar>
        {scopes.includes("devices.create") && (
          <Button
            type="primary"
            onClick={() => {
              navigate("/devices/devices/create");
            }}
          >
            Create
          </Button>
        )}

        {scopes.includes("devices.import") && (
          <Button
            type="primary"
            onClick={() => {
              navigate("/devices/devices/import");
            }}
          >
            Import
          </Button>
        )}
      </Toolbar>
      <CTable
        rowKey="activationID"
        columns={columns}
        dataSource={data}
        pagination={pagination}
        loading={loading}
        onChange={onTableChange}
        scroll={{ x: "max-content" }}
        rowClassName={(record) => (record.expired ? "expired-record" : "")}
      />
    </div>
  );
};
export default Devices;
